import { type TrackableList, type TrackableStore, TrackableType } from "@qendo/trackables";
import {
    type CountryData,
    type ListData,
    type MultiListData,
    type OptionStore,
    OptionType,
    type OptionTypeData,
} from "@qendo/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { createContext, type ReactNode, useCallback, useContext } from "react";
import { useApi } from "~/providers/ApiProvider";

const EmptyTrackables: TrackableStore = {
    [TrackableType.activity]: [],
    [TrackableType.symptom]: [],
    [TrackableType.journal]: [],
};

declare interface SystemOptionState {
    options: OptionStore;
    countries: CountryData[];
    trackables: TrackableStore;
    refreshTrackables(): Promise<void>;
}

export const EmptyOptions = Object.fromEntries(
    (Object.values(OptionType) as OptionType[]).map(<T extends OptionType>(k: T) => [k, [] as OptionTypeData<T>[]]),
) as OptionStore;

export const DefaultCountries = [
    { code: "AU", name: "Australia" },
    { code: "NZ", name: "New Zealand" },
    { code: "UK", name: "United Kingdom" },
    { code: "US", name: "United States" },
    { code: "CA", name: "Canada" },
];

export const SystemOptionContext = createContext<SystemOptionState>({
    options: EmptyOptions,
    countries: DefaultCountries,
    trackables: EmptyTrackables,
    refreshTrackables: async () => {
        return;
    },
});

export function useSystemOptions() {
    return useContext(SystemOptionContext);
}

export function SystemOptionsProvider({ children }: { children: ReactNode }) {
    const { client } = useApi();
    const queryClient = useQueryClient();

    const optionsQuery = useQuery({
        queryKey: ["options"],
        queryFn: async () => {
            return await client()
                .get("options")
                .then((response: AxiosResponse<MultiListData<OptionType, OptionTypeData>>) => ({
                    ...EmptyOptions,
                    ...response.data.data,
                }))
                .catch(() => EmptyOptions);
        },
        placeholderData: EmptyOptions,
    });
    const countryQuery = useQuery({
        queryKey: ["countries"],
        queryFn: async () => {
            return await client()
                .get("countries")
                .then((response: AxiosResponse<ListData<CountryData>>) => response.data.data)
                .catch(() => DefaultCountries);
        },
        staleTime: 1000 * 60 * 60 * 24 * 365, // 1 year
        placeholderData: DefaultCountries,
    });

    const trackablesQuery = useQuery({
        queryKey: ["trackables"],
        queryFn: async () => {
            return await client()
                .get("trackables")
                .then((response: AxiosResponse<MultiListData<TrackableType, TrackableList>>) => ({
                    ...EmptyTrackables,
                    ...response.data.data,
                }))
                .catch(() => EmptyTrackables);
        },
        placeholderData: EmptyTrackables,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies(queryClient.invalidateQueries): query client
    const refreshTrackables = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ["trackables"] });
    }, []);

    const value = {
        options: optionsQuery.data ?? EmptyOptions,
        countries: countryQuery.data ?? DefaultCountries,
        trackables: trackablesQuery.data ?? EmptyTrackables,
        refreshTrackables,
    };

    return <SystemOptionContext.Provider value={value}>{children}</SystemOptionContext.Provider>;
}
